/* istanbul ignore file */
import React from 'react';
import { COMMON_INPUT_LAYOUT_DEFAULT_PROPS, getCommonInputProps } from '../BaseInputLayout';
import { useUiTheme } from 'framework/uiTheme';
import AutosuggestComponent, { RenderSuggestionsContainerParams } from 'react-autosuggest';
import { AutosuggestionDropdownLayoutProps, AutosuggestionDropdownLayoutType, Suggestion } from 'framework/form';
import { SmallLoader } from 'framework/ui/mui-builder';
import { Box } from '@mui/material';
import { autosuggestionDropdownSx } from './autosuggestionDropdownSx';
import classNames from 'classnames';

export const renderSuggestion = (suggestion: Suggestion) => <div>{suggestion.text}</div>;

export const getSuggestionValue = (suggestion: Suggestion) => suggestion.text;

export const getSuggestionsWarning = (resultsPerPage: number) =>
  `Only ${resultsPerPage} results are shown. Please refine your search query`;

const AutosuggestionDropdownLayout: AutosuggestionDropdownLayoutType = ({
  suggestions,
  suggestionsLoaded,
  suggestionsTotalPages,
  onSuggestionsFetchRequested,
  onSuggestionsClearRequested,
  onSuggestionSelected,
  loading,
  noResultMessage,
  resultsPerPage,
  onChangeCallback,
  InputIcon,
  EndAdornment,
  renderSuggestion: renderSuggestionProp,
  suggestionsPageRef,
  isInfinityLoading,
  ...otherProps
}: AutosuggestionDropdownLayoutProps) => {
  const { BaseInputLayout } = useUiTheme();
  const props = {
    ...COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
    ...otherProps,
  };

  const renderSuggestionsContainer = ({ containerProps, children }: RenderSuggestionsContainerParams) => {
    const { key, ...otherContainerProps } = containerProps;

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 5;

      const hasNextPage = suggestionsPageRef?.current + 1 < suggestionsTotalPages;

      if (isBottom && suggestionsLoaded && hasNextPage && !loading) {
        onSuggestionsFetchRequested({ value: inputProps.value || '', reason: 'input-changed', loadNextPage: true });
      }
    };

    return (
      <div key={key} {...otherContainerProps} onScroll={isInfinityLoading ? handleScroll : () => {}}>
        {suggestions.length === 0 && suggestionsLoaded && noResultMessage && (
          <div className="react-autosuggest__no-result">{noResultMessage}</div>
        )}
        {!isInfinityLoading && suggestionsTotalPages > 1 && (
          <div className="react-autosuggest__warning-message">{getSuggestionsWarning(resultsPerPage)}</div>
        )}
        {children}
      </div>
    );
  };

  const inputProps = getCommonInputProps(props);

  return (
    <BaseInputLayout {...props}>
      <Box
        sx={[
          autosuggestionDropdownSx,
          {
            position: 'relative',
            '& .autosuggest__loader': {
              position: 'absolute',
              top: '11px',
              right: '10px',
              '& ~ .react-autosuggest__container input': { paddingRight: '40px' },
            },
            '& .autosuggest__end-adornment': {
              position: 'absolute',
              top: '50%',
              right: '10px',
              zIndex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transform: 'translateY(-50%)',
              '& ~ .react-autosuggest__container input': { paddingRight: '58px' },
            },
          },
        ]}
      >
        {InputIcon}
        {EndAdornment ? <Box className="autosuggest__end-adornment">{EndAdornment}</Box> : null}
        {loading ? <SmallLoader className="autosuggest__loader" /> : null}
        <AutosuggestComponent
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          renderSuggestionsContainer={renderSuggestionsContainer}
          onSuggestionSelected={onSuggestionSelected}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestionProp || renderSuggestion}
          inputProps={{
            ...inputProps,
            value: inputProps.value || '',
            onChange: onChangeCallback,
            placeholder: '',
            className: classNames(inputProps.className, 'autosuggestion-dropdown__input'),
          }}
        />
      </Box>
    </BaseInputLayout>
  );
};

export default AutosuggestionDropdownLayout;
